import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import CustomELearningCourseCard from 'components/courses/CustomELearningCourseCard';

import {
  currencyNumberToCurrencyString,
  displayCurrencyString,
} from 'components/helpers/currency';
import { formatDuration } from 'components/helpers/dates';

export default function BookingELearningModal({
  course,
  createELearningBooking,
  currentCredits,
  eLearningCourse,
  identifier,
  isOpen,
  setIsClosed,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.BookingELearningModal',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const handleEnrolClick = () => {
    setIsClosed();
    createELearningBooking({ course });
  };

  const { name, provider } = course?.attributes || {};

  const {
    cost,
    custom: isCustom,
    durationParts,
  } = eLearningCourse?.attributes || {};

  const { currencyCode } = currentActor.division.attributes;

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={setIsClosed}
            size='md'
          >
            {i18nTShared('cancel')}
          </TextButton>
          <FilledButton color='mint' onClick={handleEnrolClick}>
            {i18nT('button', {
              interpolation: { escapeValue: false },
              name: identifier,
            })}
          </FilledButton>
        </div>
      }
      isOpen={isOpen}
      mode='flexible'
      onClose={setIsClosed}
    >
      <div className='tw-p-8'>
        <h2 className='tw-mb-5 tw-mt-0 tw-p-0 tw-text-l tw-font-semibold tw-text-grey-900'>
          {i18nT('title', {
            courseName: name,
            interpolation: { escapeValue: false },
            name: identifier,
          })}
        </h2>
        <p className='tw-mb-5'>
          {i18nT('description', {
            interpolation: { escapeValue: false },
            name: identifier,
          })}
        </p>
        {isCustom ?
          <CustomELearningCourseCard
            courseCost={displayCurrencyString({
              string: currencyNumberToCurrencyString({
                number: cost,
                currencyCode,
              }),
              currencyCode,
            })}
            courseDuration={
              durationParts ? formatDuration({ durationParts }) : ''
            }
            courseName={name}
            courseProvider={provider}
          />
        : <div className='tw-m-0'>
            <div>
              {i18nT('creditBalance')}{' '}
              <span className='tw-font-semibold'>{currentCredits}</span>
            </div>
            <div>{i18nT('creditDeduction')}</div>
          </div>
        }
      </div>
    </ModalBox>
  );
}

BookingELearningModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsClosed: PropTypes.func.isRequired,
  identifier: PropTypes.string.isRequired,
  course: PropTypes.object,
  eLearningCourse: PropTypes.object,
  currentCredits: PropTypes.number,
  createELearningBooking: PropTypes.func.isRequired,
};
